import type {
  // 차수2d — 검수 판정 어휘는 shared 정본(콘솔이 서버에 다른 값을 보내지 않게).
  ContractRenewalReviewDecision,
  DashboardKpisDto,
  DashboardTrendMetric,
  DashboardTrendRange,
  DashboardTrendResponse,
  ExpiringContractsResponse,
  FleetPartsSummaryDto,
  FleetSolutionsSummaryDto,
  FleetDefectLotsSummaryDto,
} from '@bhops/shared';
import { apiClient } from './client';

// docs/work_log/p3-cleanup — DashboardPage 가 DashboardTrendRange 를
// `@/api/dashboard` 에서 import 하려고 했으나 re-export 가 누락돼 있었음.
// shared 타입을 그대로 통과시켜 호출처가 dashboard 모듈 한 곳에서 모든
// 트렌드 관련 타입을 가져갈 수 있도록 함.
export type { DashboardTrendMetric, DashboardTrendRange, DashboardTrendResponse };

// ============================================================
// 개선 9건 차수2c(26-08-04) §6 — 재계약율 (GET /dashboard/renewal-rate)
// ============================================================
//
// 서버 apps/api/src/dashboard/renewal-rate.service.ts 의 응답 모양과 **같아야 한다**
// (그 파일 머리 주석이 셈법의 정본이다). shared 로 올리지 않은 이유는 이 차수의 파일
// 소유 경계 때문이다 — 필드를 고칠 때 두 파일을 같이 고칠 것.
//
// 읽는 쪽이 반드시 알아야 할 두 가지:
//   • rate 가 **null** 이면 0% 가 아니라 "모수 0"(아직 만기 도래 계약이 없음)이다.
//     0 으로 접어 그리면 사업 대시보드가 "재계약율 0%"라는 거짓을 말한다.
//   • provisional = 유예기간(90일)이 아직 남은 구간 = '확정 전'.
export interface RenewalRateBucket {
  key: string;
  label: string;
  fromDate: string;
  toDate: string;
  /** 모수 — 만기 도래 로봇 대수. */
  dueRobots: number;
  /** 분자 — 유예 안에 갱신된 로봇 대수. */
  renewedRobots: number;
  /** 0~1. null = 모수 0. */
  rate: number | null;
  dueCustomers: number;
  renewedCustomers: number;
  customerRate: number | null;
  provisional: boolean;
  /** 당시 대수 스냅샷이 없어 대수 집계에서 뺀 갱신 이벤트 수(출처 구분 각주). */
  excludedNoSnapshotEvents: number;
  /** 차수2d — 아직 확정되지 않은 '확인 필요' 갱신 수. 이 행들은 집계에 **포함**돼 있다. */
  pendingReviewEvents: number;
  /** 차수2d — 사람이 '날짜 정정'으로 확정해 갱신에서 뺀 수. */
  excludedCorrectionEvents: number;
}

export interface RenewalRateResponse {
  window: RenewalRateBucket & { provisionalDueRobots: number };
  quarters: RenewalRateBucket[];
  graceDays: number;
  bySource: Record<'LIVE_SAVE' | 'BACKFILL' | 'MANUAL', number>;
  /** 갱신 원장에 처음 행이 쌓인 시각. null = 아직 한 건도 없음. */
  ledgerSince: string | null;
}

// ============================================================
// 개선 9건 차수2d(26-08-04) — 갱신 '확인 필요' 목록·확정
// ============================================================
//
// 원장이 "갱신인지 날짜 정정인지 확신할 수 없다"고 표시한 행들. 종전에는 이 표시를
// 읽는 곳이 없어 사람이 걷어낼 길이 없었다 — 그래서 재계약율이 정정을 영구히 갱신으로
// 셌다. 이 목록이 그 길이고, 확정하면 지표가 즉시 반영한다.
export interface RenewalReviewRow {
  id: string;
  contractId: string;
  contractHumanId: string;
  customerId: string;
  customerName: string;
  renewedAt: string;
  previousEndDate: string | null;
  newEndDate: string;
  previousRentalTermMonths: number | null;
  newRentalTermMonths: number | null;
  robotCountBefore: number | null;
  robotCountAfter: number | null;
  /** 왜 애매한지 — 서버가 판정할 때 적은 사유(그대로 보여 준다). */
  reviewNote: string | null;
  source: string;
}

export interface RenewalReviewListResponse {
  rows: RenewalReviewRow[];
  /** 미확정 전체 건수. rows.length 보다 크면 상한에 걸린 것 — 화면이 그 사실을 말한다. */
  total: number;
  limit: number;
}

export const dashboardApi = {
  // 이슈③ — ADMIN 은 ownerEntity 로 법인별 전환 열람. 미지정 시 기존 동작.
  // (서버가 비-ADMIN 의 타 법인 요청을 403 으로 강제하므로 클라 신뢰 안 함.)
  async kpis(ownerEntity?: string): Promise<DashboardKpisDto> {
    const { data } = ownerEntity
      ? await apiClient.get<DashboardKpisDto>('/dashboard/kpis', { params: { ownerEntity } })
      : await apiClient.get<DashboardKpisDto>('/dashboard/kpis');
    return data;
  },

  async expiringContracts(limit: number = 5): Promise<ExpiringContractsResponse> {
    const { data } = await apiClient.get<ExpiringContractsResponse>(
      '/dashboard/contracts/expiring',
      { params: { limit } },
    );
    return data;
  },

  // v0.4.0 §3.6 — FleetTracker placeholder 활성화 (부품 / 솔루션 / 결함 LOT).
  async fleetPartsSummary(): Promise<FleetPartsSummaryDto> {
    const { data } = await apiClient.get<FleetPartsSummaryDto>('/dashboard/fleet/parts-summary');
    return data;
  },

  async fleetSolutionsSummary(): Promise<FleetSolutionsSummaryDto> {
    const { data } = await apiClient.get<FleetSolutionsSummaryDto>(
      '/dashboard/fleet/solutions-summary',
    );
    return data;
  },

  async fleetDefectLots(): Promise<FleetDefectLotsSummaryDto> {
    const { data } = await apiClient.get<FleetDefectLotsSummaryDto>('/dashboard/fleet/defect-lots');
    return data;
  },

  // 개선 9건 차수2c(26-08-04) §6 — 재계약율(카드 + 분기 추이). 비율 지표라 금액 권한과
  // 무관하다(이탈률과 같은 취급).
  async renewalRate(): Promise<RenewalRateResponse> {
    const { data } = await apiClient.get<RenewalRateResponse>('/dashboard/renewal-rate');
    return data;
  },

  // 차수2d — '확인 필요' 갱신 목록(읽기 개방) · 확정(사업팀 write 역할만. 서버가 강제).
  async renewalReviewList(): Promise<RenewalReviewListResponse> {
    const { data } = await apiClient.get<RenewalReviewListResponse>('/dashboard/renewal-review');
    return data;
  },

  async decideRenewalReview(
    id: string,
    decision: ContractRenewalReviewDecision,
  ): Promise<RenewalReviewRow> {
    const { data } = await apiClient.patch<RenewalReviewRow>(`/dashboard/renewal-review/${id}`, {
      decision,
    });
    return data;
  },

  async trend(
    metric: DashboardTrendMetric,
    range: DashboardTrendRange = '30d',
  ): Promise<DashboardTrendResponse> {
    const path = `/dashboard/trends/${metric}`;
    const { data } = await apiClient.get<DashboardTrendResponse>(path, {
      params: { range },
    });
    return data;
  },
};
